import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminReportsPageStyles.scss";
import { CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { Chip } from "@progress/kendo-react-buttons";
import { GridFetchResponse, ValueObject } from "shared/types";
import ReportDetailsModal, { UpsertReportRecord } from "./ReportDetailsModal";
import { useQuery, useQueryClient } from "react-query";
import {
  useAgenciesLookupQuery,
  useResourceCategoriesLookupQuery,
} from "data/lookup/lookupQueries";
import http from "shared/http";
import useAppState from "shared/state/useAppState";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";
import { useDeleteReportMutation } from "data/resources/report";

export interface UseGetReportsForGridQueryOptions {
  title?: string;
  category?: string;
  agency?: string;
  tier?: string;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

export const useGetReportsForGridQuery = (
  params: UseGetReportsForGridQueryOptions,
) => {
  return useQuery(
    ["reports-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.fetchAsync(
        "admin/reports/search",
        params,
      );
      return response;
    },
    {
      staleTime: 30 * 1000, // Cache for 30 seconds
      enabled: true,
    },
  );
};

// Columns
interface ColumnHandlers {
  onEdit: (report: UpsertReportRecord) => void;
  onDelete: (report: UpsertReportRecord) => void;
}

const createColumns = ({
  onEdit,
  onDelete,
}: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [
      { label: "Edit", icon: "fa-solid fa-pencil" },
      { label: "Delete", icon: "fa-solid fa-trash" },
    ],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as UpsertReportRecord);
      if (label === "Delete") onDelete(dataItem as UpsertReportRecord);
    },
  },
  { field: "id", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "title",
    title: "Title",
    width: 280,
    renderCell: (data: ValueObject) => (
      <CPText noWrap style={{ textTransform: "capitalize" }} title={"test"}>
        {data.dataItem.title}
      </CPText>
    ),
  },
  {
    field: "isActive",
    title: "Status",
    width: 100,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isActive ? "Active" : "Inactive"}
        themeColor={data.dataItem.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    field: "resourceTier",
    title: "Tier",
    width: 110,
    renderCell: (data: ValueObject) => {
      const tier: string = data.dataItem.resourceTier;
      if (!tier) return null;
      const tierMap: Record<string, { color: string; bg: string }> = {
        BRONZE: { color: "#b5651d", bg: "#fdf3e7" },
        SILVER: { color: "#546e7a", bg: "#eceff1" },
        GOLD: { color: "#b8860b", bg: "#fffde7" },
      };
      const s = tierMap[tier.toUpperCase()] ?? {
        color: "#64748b",
        bg: "#f1f5f9",
      };
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: "0.73rem",
            fontWeight: 600,
            backgroundColor: s.bg,
            color: s.color,
            border: `1px solid ${s.color}40`,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: s.color,
              flexShrink: 0,
            }}
          />
          {tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()}
        </span>
      );
    },
  },
  {
    field: "categories",
    title: "Categories",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.categories ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "agencies",
    title: "Agencies",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.agencies ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "roles",
    title: "Roles",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.roles ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
];

const AdminReportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg } = useAppState();
  const confirm = useConfirm();
  const { mutate: deleteReport, isLoading: isDeleting } =
    useDeleteReportMutation();
  const [filterParams, setFilterParams] =
    React.useState<UseGetReportsForGridQueryOptions>({ take: 20, skip: 0 });
  const { data: categories, isLoading: isCategoriesLoading } =
    useResourceCategoriesLookupQuery();
  const { data: agencies, isLoading: isAgenciesLoading } =
    useAgenciesLookupQuery();
  const { data: reports, isFetching: isReportsLoading } =
    useGetReportsForGridQuery(filterParams);

  const [selectedReport, setSelectedReport] =
    React.useState<UpsertReportRecord | null>(null);

  const handleDeleteReport = (report: UpsertReportRecord) => {
    confirm((confirmed) => {
      if (confirmed) {
        deleteReport(report.id, {
          onSuccess: () => {
            setToastMsg("Report deleted successfully", "success");
            queryClient.invalidateQueries(["reports-grid"]);
          },
          onError: () => {
            setToastMsg("Failed to delete report", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${report.title}"?`);
  };

  const adapter = useCPGridV2({
    columns: createColumns({
      onEdit: setSelectedReport,
      onDelete: handleDeleteReport,
    }),
    data: reports?.records ?? [],
    showLoader: isReportsLoading,
    totalRecords: reports?.totalRecords ?? 0,
  });

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(() => {
    return [
      {
        filterType: "text",
        label: "Title",
        columnKey: "title",
        placeholder: "e.g. Sales Dashboard",
      },
      {
        filterType: "dropdown",
        label: "Category",
        columnKey: "categories",
        options:
          categories?.map((category) => ({
            label: category,
            value: category,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Agency",
        columnKey: "agencies",
        options:
          agencies?.map((x) => ({
            label: x.agency,
            value: x.agency,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Status",
        columnKey: "isActive",
        options: [
          { label: "Active", value: "true" },
          { label: "Inactive", value: "false" },
        ],
      },
      {
        filterType: "dropdown",
        label: "Tier",
        columnKey: "resourceTier",
        options: [
          { label: "Bronze", value: "BRONZE" },
          { label: "Silver", value: "SILVER" },
          { label: "Gold", value: "GOLD" },
        ],
      },
    ];
  }, [categories, agencies]);

  return (
    <div className="admin-reports-page">
      <CPGridV2
        name="admin-reports-grid"
        title="Report Management"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetReportsForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        onRefresh={() => queryClient.invalidateQueries(["reports-grid"])}
        renderActions={() => (
          <CPButton
            fillMode="flat"
            size="small"
            onClick={() => setSelectedReport(new UpsertReportRecord())}
          >
            + Add Report
          </CPButton>
        )}
      />

      <ReportDetailsModal
        key={selectedReport?.id}
        selectedReport={selectedReport}
        onClose={() => {
          setSelectedReport(null);
        }}
        onSave={() => {
          setSelectedReport(null);
          queryClient.invalidateQueries(["reports-grid"]);
        }}
        onDelete={() => {
          setSelectedReport(null);
          queryClient.invalidateQueries(["reports-grid"]);
        }}
      />
    </div>
  );
};

export { AdminReportsPage };
export default AdminReportsPage;
